/**
 * AgentRunResumeManager
 *
 * Headless component (renders null) that restores agent-run progress after a
 * hard refresh. agentRunStore/activityStore are in-memory only, so a reload
 * drops every live SSE subscription even though the backend run keeps
 * executing. Once the auth context is ready, this asks the backend for runs
 * still in status 'running' and reattaches each via agentRunStore.reconnectRun,
 * which resumes the live stream (GET /api/agent-runs/{id}/stream) and re-seeds
 * the same state that powers the agent workspace, the agents list badges, and
 * the header ActivityDropdown.
 *
 * Mount once in AppContent BEFORE <Router> (next to the retrieval managers) so
 * the resumed streams survive route changes.
 */

import { useEffect, useRef } from 'react';
import { useSkillsRequestContext } from '@/services/skills';
import { apiGet } from '@/services/studio/client';
import { normalizeAgentRunListResponse } from '@/services/agents/normalize';
import { useAgentRunStore } from '@/stores/agentRunStore';

// Runs stuck 'running' in the DB (e.g. instance killed mid-run before a
// terminal status was persisted) must not be resurrected as live indicators.
// The backend hard-caps a run at AGENTS_RUN_TIMEOUT_SECONDS (500s); anything
// older than that plus margin is a zombie row, not a run worth reattaching to.
const MAX_RESUME_AGE_MS = 15 * 60 * 1000;

function startedWithinResumeWindow(startedAt: string): boolean {
  // SQLite rows serialize as "YYYY-MM-DD HH:MM:SS" (UTC, no zone); Postgres as ISO.
  const iso = startedAt.includes('T') ? startedAt : `${startedAt.replace(' ', 'T')}Z`;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return true; // unparseable — let the stream decide
  return Date.now() - ts <= MAX_RESUME_AGE_MS;
}

export function AgentRunResumeManager() {
  const { orgId, userId } = useSkillsRequestContext();
  const resumedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!orgId || !userId) return;
    const contextKey = `${orgId}:${userId}`;
    if (resumedForRef.current === contextKey) return;
    resumedForRef.current = contextKey;

    let cancelled = false;
    void apiGet<unknown>('/api/agent-runs?status=running&limit=25')
      .then((payload) => {
        if (cancelled) return;
        const { runs } = normalizeAgentRunListResponse(payload);
        const { reconnectRun } = useAgentRunStore.getState();
        const seenAgents = new Set<string>();
        // List is newest-first; resume at most one run per agent (the store is
        // keyed by agent id, so a second reconnect would just replace the first).
        for (const run of runs) {
          // The run store must be keyed by the frontend agent id, which is the
          // SLUG (normalizeAgentRecord sets agent.id = slug) — run.agent_id is
          // the backend UUID and would never match the detail page / list
          // subscriptions. surface_slug is the run's slug snapshot.
          const storeKey = run.surface_slug || run.agent_id;
          if (!run.id || !storeKey || seenAgents.has(storeKey)) continue;
          if (!startedWithinResumeWindow(run.started_at)) continue;
          seenAgents.add(storeKey);
          reconnectRun(storeKey, run.surface_name || 'Agent', run.id);
        }
      })
      .catch((err) => {
        // Non-fatal: worst case the UI just doesn't show resumed progress.
        console.warn('[AgentRunResumeManager] failed to restore active runs', err);
        resumedForRef.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, userId]);

  return null;
}
